import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from || '/admin/incentivo/dashboard';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    setTimeout(() => {
      const result = login(email, senha);
      setIsLoading(false);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Erro ao fazer login');
      }
    }, 600);
  };

  const quickLogin = (demoEmail: string) => {
    setEmail(demoEmail);
    setError('');
    if (demoEmail === 'admin@getpoints.com.br') {
      setSenha('123456');
    } else if (demoEmail === 'admin@inovacaodigital.com.br') {
      setSenha('inovacao2024');
    } else if (demoEmail === 'admin@techbrasil.com.br') {
      setSenha('tech2024');
    } else if (demoEmail === 'admin@sistemasintegrados.com.br') {
      setSenha('sistemas2024');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-building-line text-2xl text-white"></i>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Área do Cliente</h1>
          <p className="text-sm text-gray-500 mt-1">Portal de Gestão de Campanhas e Premiações</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center space-x-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
              </div>
              <p className="text-xs text-red-600 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Email da Empresa</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@suaempresa.com.br"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Senha</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="••••••"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm rounded-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap shadow-md"
            >
              {isLoading ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Entrando...</span>
                </span>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          {/* Quick login for demo */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 text-center mb-3 uppercase tracking-wider">Acesso rápido — Empresas demo</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => quickLogin('admin@getpoints.com.br')}
                className="p-2 bg-orange-50 hover:bg-orange-100 rounded-lg text-[10px] text-orange-700 font-medium transition-all cursor-pointer text-left border border-orange-100"
              >
                <span className="font-bold text-orange-800 block">getPoints</span>
                <span className="text-orange-400">admin@getpoints...</span>
              </button>
              <button
                onClick={() => quickLogin('admin@inovacaodigital.com.br')}
                className="p-2 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-[10px] text-emerald-700 font-medium transition-all cursor-pointer text-left border border-emerald-100"
              >
                <span className="font-bold text-emerald-800 block">Inovação Digital</span>
                <span className="text-emerald-400">admin@inovacao...</span>
              </button>
              <button
                onClick={() => quickLogin('admin@techbrasil.com.br')}
                className="p-2 bg-sky-50 hover:bg-sky-100 rounded-lg text-[10px] text-sky-700 font-medium transition-all cursor-pointer text-left border border-sky-100"
              >
                <span className="font-bold text-sky-800 block">Tech Brasil</span>
                <span className="text-sky-400">admin@techbr...</span>
              </button>
              <button
                onClick={() => quickLogin('admin@sistemasintegrados.com.br')}
                className="p-2 bg-violet-50 hover:bg-violet-100 rounded-lg text-[10px] text-violet-700 font-medium transition-all cursor-pointer text-left border border-violet-100"
              >
                <span className="font-bold text-violet-800 block">Sistemas Integrados</span>
                <span className="text-violet-400">admin@sistemas...</span>
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Esta é a área exclusiva para empresas clientes da plataforma.
          <br />
          Acesso da equipe getPoints? <a href="/backoffice/login" className="text-orange-500 hover:text-orange-600 underline">Backoffice</a>
        </p>
      </div>
    </div>
  );
}